import { useState, useEffect } from "react";
import { ArrowUp, ArrowRight, Download, Shield, Droplets, Thermometer, Zap, Heart, Sparkles, Wrench, FileText, HelpCircle, CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { FeatureCardSimple } from "@/components/ui/feature-card-simple";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Navigation from "@/components/Navigation";
import { SEO } from "@/components/SEO";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import { useOptimizedObserver } from "@/hooks/useOptimizedObserver";
import ImageLightbox from "@/components/ui/image-lightbox";
import { ProductGallery } from "@/components/ui/product-gallery";
import PDFDownloadButton from "@/components/ui/pdf-download-button";
import OptimizedVideo from "@/components/ui/optimized-video";
import FAQ from "@/components/FAQ";
import { productFAQs } from "@/data/faqData";
import { RelatedProducts } from "@/components/RelatedProducts";
import { Footer } from "@/components/Footer";
import { ShippingTimeInfo } from "@/components/ShippingTimeInfo";
import { LocalizedLink } from "@/components/LocalizedLink";
import { useI18n } from "@/i18n";
import lunaMainImage from "@/assets/luna-m9-main-optimized.webp";
import lunaMainImageFallback from "@/assets/luna-m9-main.jpg";
import lunaAngleImage from "@/assets/luna-m9-angle.jpg";
import lunaDisplayImage from "@/assets/luna-m9-display.webp";
import lunaInstalledImage from "@/assets/luna-m9-installed.jpg";
import lunaControlsImage from "@/assets/luna-m9-controls.png";

import { PriceFrom } from "@/components/products/PriceFrom";
export default function Luna() {
  const [showBackToTop, setShowBackToTop] = useState(false);
  const { t } = useI18n();
  useScrollToTop(); // Scroll to top when page loads
  useOptimizedObserver(); // Initialize observer for fade-in animations

  useEffect(() => {
    let rafId: number | null = null;
    
    const handleScroll = () => {
      if (rafId) return;
      
      rafId = requestAnimationFrame(() => {
        setShowBackToTop(window.scrollY > 400);
        rafId = null;
      });
    };
    
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDownload = (fileName: string, displayName: string) => {
    const link = document.createElement('a');
    link.href = fileName;
    link.download = displayName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const productImages = [
    { src: lunaMainImage, alt: "LUNA M9 luxury smart toilet with instant heating and wellness spa features", title: "Main Design" },
    { src: lunaAngleImage, alt: "LUNA M9 luxury smart toilet profile view showing elegant design and digital controls", title: "Profile View" },
    { src: lunaDisplayImage, alt: "LUNA M9 luxury smart toilet digital display with temperature and pressure controls", title: "Digital Controls" },
    { src: lunaInstalledImage, alt: "LUNA M9 luxury smart toilet installed in premium modern bathroom setting", title: "Installation" },
    { src: lunaControlsImage, alt: "LUNA M9 luxury smart toilet wireless remote control panel with advanced bidet functions", title: "Control Panel" }
  ];

  const keyFeatures = [
    "Instant heating technology for immediate comfort",
    "Digital display with temperature control",
    "Smart bidet system with rear and front wash",
    "Integrated warm air dryer",
    "Auto open/close functionality",
    "Night light with soft illumination",
    "Self-cleaning nozzles",
    "Memory settings for personalized experience",
    "Energy-saving eco mode",
    "Antimicrobial seat material"
  ];

  const technicalSpecs = [
    { label: "Model", value: "M9" },
    { label: "Dimensions", value: "715 × 625 × 465 mm" },
    { label: "Heating Type", value: "Instant heating system" },
    { label: "S-trap Rough-in", value: "300±400 mm (customizable 200-450 mm)" },
    { label: "P-trap Rough-in", value: "~100 mm" },
    { label: "Water Pressure", value: "0.07-0.75 MPa" },
    { label: "Power Consumption", value: "1200W max" },
    { label: "Water Protection", value: "IPX4 Waterproof" },
    { label: "Seat Material", value: "Antimicrobial composite" },
    { label: "Weight", value: "35kg" }
  ];

  const installationRequirements = [
    {
      title: "Electrical Requirements",
      items: [
        "220-240V AC power supply",
        "Dedicated GFCI outlet required",
        "Professional electrical installation recommended"
      ]
    },
    {
      title: "Plumbing Requirements",
      items: [
        "Water supply line (cold water)",
        "Drain connection compatible with S-trap or P-trap",
        "Water pressure: 0.07-0.75 MPa",
        "Shut-off valve accessible for maintenance"
      ]
    },
    {
      title: "Space Requirements",
      items: [
        "Minimum 715mm width clearance",
        "625mm depth from wall center",
        "465mm height clearance",
        "Additional space for seat opening/closing"
      ]
    }
  ];

  const warrantyInfo = [
    {
      component: "Electronic Components",
      period: "3 years",
      coverage: "Manufacturing defects, control panel, sensors"
    },
    {
      component: "Ceramic Body",
      period: "5 years",
      coverage: "Structural integrity, finish durability"
    },
    {
      component: "Heating System",
      period: "2 years",
      coverage: "Instant heating mechanism, temperature control"
    },
    {
      component: "Water System",
      period: "2 years",
      coverage: "Pump, nozzles, bidet functionality"
    }
  ];

  const faqItems = [
    {
      question: "What are the exact dimensions and will it fit my bathroom?",
      answer: "The LUNA M9 measures 715mm × 625mm × 465mm. It requires a minimum clearance width of 715mm and depth of 625mm from the wall center. Please measure your space carefully and consult our installation guide."
    },
    {
      question: "What type of rough-in do I need?",
      answer: "The M9 supports both S-trap (300±400mm, customizable 200-450mm) and P-trap (~100mm) configurations. The rough-in can be customized during installation to fit your existing plumbing."
    },
    {
      question: "Do I need special electrical work?",
      answer: "Yes, you'll need a dedicated 220-240V GFCI outlet near the toilet. Professional electrical installation is recommended for safety and warranty compliance."
    },
    {
      question: "How does the instant heating work?",
      answer: "The M9 features an advanced instant heating system that provides immediate warm water without a storage tank, ensuring consistent temperature and energy efficiency."
    },
    {
      question: "Can I install it myself?",
      answer: "While the M9 comes with detailed installation instructions, we recommend professional installation to ensure proper plumbing, electrical connections, and warranty coverage."
    },
    {
      question: "What maintenance is required?",
      answer: "Regular cleaning with mild soap, monthly nozzle cleaning cycles (automated), and annual professional maintenance check-ups are recommended for optimal performance."
    }
  ];

  const productStructuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "LUNA M9 Smart Toilet",
    "description": "Experience the LUNA M9 smart toilet with instant heating, digital display, spa-like bidet system, and wellness features. European wellness technology for sophisticated living.",
    "brand": {
      "@type": "Brand",
      "name": "Aureum VII Trading",
      "logo": "https://www.aureum7trading.com/lovable-uploads/aureum-vii-trading-logo.png"
    },
    "manufacturer": {
      "@type": "Organization",
      "name": "Aureum VII Trading",
      "url": "https://www.aureum7trading.com"
    },
    "image": [
      "https://www.aureum7trading.com/images/products/luna-m9-main.jpg",
      "https://www.aureum7trading.com/images/products/luna-m9-angle.jpg",
      "https://www.aureum7trading.com/images/products/luna-m9-display.jpg"
    ],
    "sku": "LUNA-M9",
    "mpn": "M9",
    "gtin13": "8001234567890",
    "productID": "LUNA-M9",
    "category": "Smart Toilets > Luxury Bathroom Fixtures",
    "material": "Premium Ceramic, Antimicrobial Composite",
    "color": "White",
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock",
      "priceCurrency": "EUR",
      "price": 600,
      "priceValidUntil": "2026-12-31",
      "url": "https://www.aureum7trading.com/products/luna",
      "itemCondition": "https://schema.org/NewCondition",
      "seller": {
        "@type": "Organization",
        "name": "Aureum VII Trading",
        "url": "https://www.aureum7trading.com"
      },
      "shippingDetails": {
        "@type": "OfferShippingDetails",
        "shippingRate": {
          "@type": "MonetaryAmount",
          "value": "0",
          "currency": "EUR"
        },
        "shippingDestination": {
          "@type": "DefinedRegion",
          "addressCountry": ["ES", "DE", "FR", "IT", "GB", "PT", "NL", "BE", "AT"]
        },
        "deliveryTime": {
          "@type": "ShippingDeliveryTime",
          "handlingTime": {
            "@type": "QuantitativeValue",
            "minValue": 1,
            "maxValue": 3,
            "unitCode": "DAY"
          },
          "transitTime": {
            "@type": "QuantitativeValue",
            "minValue": 5,
            "maxValue": 14,
            "unitCode": "DAY"
          }
        }
      },
      "hasMerchantReturnPolicy": {
        "@type": "MerchantReturnPolicy",
        "@id": "https://www.aureum7trading.com/return-policy",
        "applicableCountry": ["ES", "DE", "FR", "IT", "GB", "PT", "NL", "BE", "AT"],
        "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
        "merchantReturnDays": 14,
        "returnMethod": "https://schema.org/ReturnByMail",
        "returnFees": "https://schema.org/FreeReturn",
        "itemCondition": "https://schema.org/NewCondition",
        "refundType": "https://schema.org/FullRefund"
      }
    },
    "additionalProperty": [
      {
        "@type": "PropertyValue",
        "name": "Dimensions",
        "value": "715 × 625 × 465 mm"
      },
      {
        "@type": "PropertyValue",
        "name": "Heating Type",
        "value": "Instant heating system"
      },
      {
        "@type": "PropertyValue",
        "name": "Power Consumption",
        "value": "1200W max"
      },
      {
        "@type": "PropertyValue",
        "name": "Water Protection",
        "value": "IPX4 Waterproof"
      },
      {
        "@type": "PropertyValue",
        "name": "Warranty",
        "value": "5 years comprehensive"
      },
      {
        "@type": "PropertyValue",
        "name": "Weight",
        "value": "35kg"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary via-background to-muted text-foreground">
      <SEO
        title="LUNA M9 Smart Toilet | Spa Bidet & Instant Heating"
        description="LUNA M9 smart toilet: instant heating, front and rear spa bidet wash, warm-air dryer, auto lid and LED night light. S-trap and P-trap compatible, IPX4."
        keywords="smart toilet, bidet toilet, intelligent toilet, heated toilet seat, self cleaning toilet, toilet with bidet, luxury smart toilet, automated toilet, LUNA M9 wellness, wellness spa toilet, instant heating bidet, therapeutic bathroom technology, sanitary wellness technology, bathroom wellness systems"
        structuredData={productStructuredData}
        url="/products/luna"
        image="https://www.aureum7trading.com/images/products/luna-series-product-main.jpg"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Smart Toilets", url: "/smart-toilets" },
          { name: "LUNA M9", url: "/products/luna" }
        ]}
      />
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-24 pb-12 px-6 lg:px-12">
        <div className="container mx-auto max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="fade-in-observer">
              <Badge variant="outline" className="border-primary/30 text-primary bg-primary/5 mb-4 font-medium">
                AVT Premium Series
              </Badge>
              <h1 className="font-playfair text-5xl md:text-6xl font-light mb-6 text-primary">
                {t.productPages.luna.heroTitle}
                <span className="block text-2xl md:text-3xl text-muted-foreground font-medium mt-2">
                  {t.productPages.luna.heroSubtitle}
                </span>
              </h1>
              <p className="font-inter text-xl text-muted-foreground mb-8 leading-relaxed">
                {t.productPages.luna.heroDescription}
              </p>

              <PriceFrom price={600} />

              {/* Features List */}
              <div className="mb-8 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-accent rounded-full"></div>
                  <span className="font-inter text-muted-foreground">{t.productPages.luna.features.heatedSeat}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-accent rounded-full"></div>
                  <span className="font-inter text-muted-foreground">{t.productPages.luna.features.washModes}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-accent rounded-full"></div>
                  <span className="font-inter text-muted-foreground">{t.productPages.luna.features.dryingFunction}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-accent rounded-full"></div>
                  <span className="font-inter text-muted-foreground">{t.productPages.luna.features.modernDesign}</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <PDFDownloadButton productName="LUNA" />
                <Button variant="outline" asChild>
                  <LocalizedLink to="/smart-toilets">{t.productPages.luna.cta.backToCollections}</LocalizedLink>
                </Button>
              </div>
            </div>
            <div className="fade-in-observer">
              <div className="relative">
                <picture>
                  <source srcSet={lunaMainImage} type="image/webp" />
                  <img 
                    src={lunaMainImageFallback} 
                    alt="LUNA M9 wellness spa smart toilet featuring therapeutic massage functions, instant water heating technology, advanced air purification system, aromatherapy integration, and premium comfort-focused design for ultimate relaxation"
                    className="w-full h-auto rounded-2xl shadow-luxury"
                    width="600"
                    height="800"
                    loading="eager"
                  />
                </picture>
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent rounded-2xl" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Video */}
      <section className="py-16 px-6 lg:px-12 bg-white/60 backdrop-blur-sm">
        <div className="container mx-auto max-w-5xl">
          <div className="fade-in-observer text-center mb-12">
            <h2 className="font-playfair text-3xl font-medium text-primary mb-4">See LUNA in Action</h2>
            <p className="font-inter text-muted-foreground max-w-2xl mx-auto">
              Discover the advanced features and elegant design of the LUNA M9 smart toilet system.
            </p>
          </div>
          <OptimizedVideo
            src="/videos/luna-m9-demo.mp4"
            poster={lunaDisplayImage}
            controls
            className="aspect-video rounded-2xl overflow-hidden shadow-luxury bg-slate-100"
            aria-label="LUNA M9 smart toilet demonstration video"
          />
        </div>
      </section>

      {/* Product Gallery */}
      <section className="py-16 px-6 lg:px-12">
        <div className="container mx-auto max-w-7xl">
          <div className="fade-in-observer">
            <h2 className="font-playfair text-3xl font-medium text-primary mb-12 text-center">Product Gallery</h2>
             <ProductGallery images={productImages.slice(0, 5)} className="max-w-4xl mx-auto" />
          </div>
        </div>
      </section>

      {/* Product Information Tabs */}
      <section className="py-16 px-6 lg:px-12 bg-white/60 backdrop-blur-sm">
        <div className="container mx-auto max-w-7xl">
          <div className="fade-in-observer">
            <Tabs defaultValue="specifications" className="w-full">
              <TabsList className="grid w-full grid-cols-4 lg:w-fit lg:mx-auto mb-12">
                <TabsTrigger value="specifications" className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  <span className="hidden sm:inline">Specifications</span>
                </TabsTrigger>
                <TabsTrigger value="features" className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  <span className="hidden sm:inline">Features</span>
                </TabsTrigger>
                <TabsTrigger value="installation" className="flex items-center gap-2">
                  <Wrench className="w-4 h-4" />
                  <span className="hidden sm:inline">Installation</span>
                </TabsTrigger>
                <TabsTrigger value="warranty" className="flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  <span className="hidden sm:inline">Warranty</span>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="specifications" className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto mt-8">
                {technicalSpecs.map((spec, index) => (
                  <div key={index} className="bg-white/80 backdrop-blur-sm shadow-soft rounded-lg p-6">
                    <p className="font-inter font-semibold text-primary mb-2">{spec.label}</p>
                    <p className="font-inter text-muted-foreground">{spec.value}</p>
                  </div>
                ))}
              </TabsContent>

              <TabsContent value="features" className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto mt-8">
                {keyFeatures.map((feature, index) => (
                  <div key={index} className="bg-white/80 backdrop-blur-sm shadow-soft rounded-lg p-6 flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                    <p className="font-inter text-muted-foreground">{feature}</p>
                  </div>
                ))}
              </TabsContent>

              <TabsContent value="installation" className="mt-8">
                <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
                  {installationRequirements.map((req, index) => (
                    <div key={index} className="bg-white/80 backdrop-blur-sm shadow-soft rounded-lg p-6">
                      <h3 className="font-inter font-semibold text-primary mb-4 flex items-center gap-2">
                        <Wrench className="w-5 h-5" />
                        {req.title}
                      </h3>
                       <ul className="space-y-2">
                        {req.items.map((item, itemIndex) => (
                          <li key={itemIndex} className="font-inter text-muted-foreground text-sm flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
                <div className="max-w-2xl mx-auto mt-8 border-destructive/20 bg-destructive/5 rounded-lg p-6 flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center text-sm font-bold flex-shrink-0">!</div>
                  <div>
                    <h4 className="font-inter font-semibold text-foreground mb-2">Professional Installation Recommended</h4>
                    <p className="font-inter text-muted-foreground text-sm">
                      For optimal performance and warranty coverage, we recommend professional installation by a qualified plumber and electrician.
                    </p>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="warranty" className="mt-8">
                <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                  {warrantyInfo.map((warranty, index) => (
                  <div key={index} className="bg-white/80 backdrop-blur-sm shadow-soft rounded-lg p-6">
                      <div className="flex items-start gap-3 mb-3">
                        <Shield className="w-5 h-5 text-accent mt-1" />
                        <div>
                          <h3 className="font-inter font-semibold text-primary">{warranty.component}</h3>
                          <Badge variant="secondary" className="mt-1">{warranty.period}</Badge>
                        </div>
                      </div>
                      <p className="font-inter text-muted-foreground text-sm">{warranty.coverage}</p>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-6 lg:px-12">
        <div className="container mx-auto max-w-4xl">
          <div className="fade-in-observer">
            <div className="text-center mb-12">
              <h2 className="font-playfair text-3xl font-medium text-primary mb-4 flex items-center justify-center gap-2">
                <HelpCircle className="w-8 h-8" />
                Frequently Asked Questions
              </h2>
              <p className="font-inter text-muted-foreground">
                Everything you need to know about sizing, installation, and the LUNA M9
              </p>
            </div>
            <div className="space-y-4">
              {faqItems.map((faq, index) => (
                <div key={index} className="border-0 bg-white/80 backdrop-blur-sm shadow-soft rounded-lg p-6">
                  <h3 className="font-inter font-semibold text-primary mb-3">{faq.question}</h3>
                  <p className="font-inter text-muted-foreground leading-relaxed">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Shipping & Delivery Information */}
      <section className="py-16 px-6 lg:px-12">
        <div className="container mx-auto max-w-7xl fade-in-observer">
          <ShippingTimeInfo variant="detailed" />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 lg:px-12 bg-gradient-to-r from-primary via-primary to-accent text-primary-foreground">
        <div className="container mx-auto max-w-4xl text-center fade-in-observer">
          <h2 className="font-playfair text-4xl font-light mb-6">Ready to Experience LUNA M9?</h2>
          <p className="font-inter text-lg opacity-90 mb-8 leading-relaxed">
            Transform your bathroom with intelligent luxury, instant heating, and premium comfort features.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild className="bg-background text-foreground hover:bg-background/90 font-inter font-semibold px-8 py-3 shadow-lg">
              <Link to="/contact">
                Contact Sales Team
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
            <Button 
              variant="outline" 
              className="border-primary-foreground text-primary-foreground hover:bg-primary-foreground/10 font-inter px-8 py-3"
              onClick={() => handleDownload('/files/RRL_Catalog.pdf', 'LUNA_M9_Brochure.pdf')}
            >
              <Download className="w-4 h-4 mr-2" />
              Download Brochure
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQ 
        title="LUNA M9 Frequently Asked Questions"
        subtitle="Everything you need to know about the LUNA M9 smart toilet system"
        items={productFAQs.luna}
        className="bg-background"
      />

      {/* Related Products */}
      <RelatedProducts currentProduct="LUNA" />

      {/* Footer */}
      <Footer />

      {/* Back to Top */}
      {showBackToTop && (
        <Button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full p-3 shadow-luxury z-50"
        >
          <ArrowUp className="w-5 h-5" />
        </Button>
      )}
    </div>
  );
}